import { useEffect, useState } from "react";
import { Save, RefreshCw, Plus, Trash2, Cloud, Server } from "lucide-react";
import { Modal, InputNumber } from "antd";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import {
  PRICING_RATES_URL,
  PRICING_RATES_ROWS_URL,
} from "../../constants/api";

const PRODUCTS = [
  { key: "cms",  label: "Clinical Management System" },
  { key: "hims", label: "Hospital Information Management System" },
];

// The deployment axis. Cloud is what the screen priced before on-premise
// existed, so it stays the tab you land on.
const DEPLOYMENTS = [
  { key: "cloud",  label: "On Cloud",   icon: Cloud,  note: "Hosted by us. Priced across the full plan ladder." },
  { key: "onprem", label: "On Premise", icon: Server, note: "Installed on the hospital's own servers. Enterprise only." },
];

const PLANS = [
  { key: "essential",  label: "Essential" },
  { key: "advanced",   label: "Advanced" },
  { key: "enterprise", label: "Enterprise" },
];

// Which plan columns a deployment shows. The server owns this decision — it
// returns the list on every load — and this is only the value used before the
// first response lands, so the table never renders a column the save endpoint
// would refuse to write.
const DEFAULT_PLAN_KEYS = {
  cloud:  ["essential", "advanced", "enterprise"],
  onprem: ["enterprise"],
};

export default function ProductPricing() {
  const { fetch, update, create, remove } = useApiMethods();

  // rates: [{op_count: 5000, essential: "22214", advanced: "32104", enterprise: "41892"}, ...]
  const [rates, setRates]               = useState([]);
  const [activeProduct, setActiveProduct] = useState("cms");
  const [activeDeployment, setActiveDeployment] = useState("cloud");
  const [planKeys, setPlanKeys]         = useState(DEFAULT_PLAN_KEYS.cloud);
  // Resolved per request from the load below rather than cached at login: a
  // session that signed in before the flag was switched on would otherwise
  // carry a stale copy until it signed in again.
  const [onPremAvailable, setOnPremAvailable] = useState(false);
  const [loading, setLoading]           = useState(false);
  const [saving, setSaving]             = useState(false);

  // Add row modal
  const [addRowOpen, setAddRowOpen]     = useState(false);
  const [newOpCount, setNewOpCount]     = useState(null);
  const [addingRow, setAddingRow]       = useState(false);

  const load = async (product, deployment) => {
    const p = product || activeProduct;
    const d = deployment || activeDeployment;
    setLoading(true);
    const res = await fetch(`${PRICING_RATES_URL}?product=${p}&deployment=${d}`);
    setLoading(false);
    if (res?.status === "success") {
      setRates(res.data || []);
      setPlanKeys(res.plans?.length ? res.plans : DEFAULT_PLAN_KEYS[d]);
      if (res.on_premise_available !== undefined) {
        setOnPremAvailable(!!res.on_premise_available);
      }
    } else {
      ToastMsg(res?.message || "Failed to load", "error");
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const switchProduct = (p) => {
    setActiveProduct(p);
    load(p, activeDeployment);
  };

  const switchDeployment = (d) => {
    setActiveDeployment(d);
    // Swap the columns immediately so the old deployment's plans are not left
    // on screen holding the previous deployment's prices while the fetch runs.
    setPlanKeys(DEFAULT_PLAN_KEYS[d]);
    setRates([]);
    load(activeProduct, d);
  };

  const activeDeploymentMeta = DEPLOYMENTS.find(d => d.key === activeDeployment);

  const setPrice = (opCount, plan, value) => {
    setRates(prev => prev.map(r =>
      r.op_count === opCount ? { ...r, [plan]: value } : r
    ));
  };

  const save = async () => {
    setSaving(true);
    const res = await update(PRICING_RATES_URL, {
      product: activeProduct,
      deployment: activeDeployment,
      rates: rates,
    });
    setSaving(false);
    if (res?.status === "success") {
      ToastMsg("Rates saved", "success");
    } else {
      ToastMsg(res?.message || "Failed to save", "error");
    }
  };

  const handleAddRow = async () => {
    if (!newOpCount || newOpCount <= 0) return ToastMsg("Enter a valid OP count", "error");
    if (rates.some(r => r.op_count === newOpCount)) return ToastMsg(`Row ${newOpCount.toLocaleString("en-IN")} already exists`, "error");
    setAddingRow(true);
    const res = await create(PRICING_RATES_ROWS_URL, {
      product: activeProduct,
      deployment: activeDeployment,
      op_count: newOpCount,
    });
    setAddingRow(false);
    if (res?.status === "success") {
      ToastMsg(`Row ${newOpCount.toLocaleString("en-IN")} added`, "success");
      setAddRowOpen(false);
      setNewOpCount(null);
      load();
    } else {
      ToastMsg(res?.message || "Failed to add row", "error");
    }
  };

  const handleDeleteRow = (opCount) => {
    Modal.confirm({
      title: `Delete row ${opCount.toLocaleString("en-IN")} OP count?`,
      // Named, because the delete is scoped to the deployment you are looking
      // at — the same OP count on the other tab is left alone.
      content: `This will remove pricing for this OP count across all plans of ${activeDeploymentMeta?.label || "this deployment"}.`,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        const res = await remove(`${PRICING_RATES_ROWS_URL}${opCount}/?product=${activeProduct}&deployment=${activeDeployment}`);
        if (res?.status === "success") {
          ToastMsg(`Row ${opCount.toLocaleString("en-IN")} deleted`, "success");
          load();
        } else {
          ToastMsg(res?.message || "Failed to delete", "error");
        }
      },
    });
  };

  // A tenant that does not sell its software for on-premise install sees the
  // screen exactly as it was before this tab existed — one deployment is not a
  // choice, so it is not drawn as one.
  const deployments = DEPLOYMENTS.filter(d => d.key === "cloud" || onPremAvailable);
  const columns = PLANS.filter(pl => planKeys.includes(pl.key));

  if (loading && !rates.length) {
    return <div className="p-6 text-sm text-gray-500">Loading...</div>;
  }

  return (
    <ContainerCard>
      <div className="px-6 py-4 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Tier Pricing</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Yearly price per OP count and plan. Enter prices directly for each tier.
            </p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => load()}>
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setAddRowOpen(true); setNewOpCount(null); }}>
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Row
            </Button>
            <Button size="sm" onClick={save} disabled={saving}>
              <Save className="h-3.5 w-3.5 mr-1" /> {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>

        {/* Deployment tabs — only drawn when there is more than one to pick */}
        {deployments.length > 1 && (
          <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 w-fit">
            {deployments.map(d => {
              const Icon = d.icon;
              return (
                <button
                  key={d.key}
                  onClick={() => switchDeployment(d.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors
                    ${activeDeployment === d.key
                      ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"}`}
                >
                  <Icon className="h-3.5 w-3.5" /> {d.label}
                </button>
              );
            })}
            <span className="pl-2 pr-1 text-[11px] text-gray-400">{activeDeploymentMeta?.note}</span>
          </div>
        )}

        {/* Product tabs */}
        <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
          {PRODUCTS.map(p => (
            <button
              key={p.key}
              onClick={() => switchProduct(p.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition
                ${activeProduct === p.key
                  ? "border-indigo-600 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Price table */}
        <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-900/50">
                <th className="text-left px-4 py-2.5 font-semibold text-gray-600 dark:text-gray-400 w-40">OP Count</th>
                {columns.map(pl => (
                  <th key={pl.key} className="text-right px-4 py-2.5 font-semibold text-gray-900 dark:text-white">{pl.label}</th>
                ))}
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {rates.map(row => (
                <tr key={row.op_count} className="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50/50 dark:hover:bg-gray-700/30">
                  <td className="px-4 py-1.5 font-medium text-gray-800 dark:text-gray-200 tabular-nums">
                    {row.op_count.toLocaleString("en-IN")}
                  </td>
                  {columns.map(pl => (
                    <td key={pl.key} className="px-4 py-1.5">
                      <InputNumber
                        className="w-full text-right"
                        min={0}
                        value={Number(row[pl.key] || 0) || null}
                        onChange={(val) => setPrice(row.op_count, pl.key, val || 0)}
                        formatter={v => v ? Number(v).toLocaleString("en-IN") : ""}
                        parser={v => v.replace(/[^\d.]/g, "")}
                        controls={false}
                        style={{ textAlign: "right" }}
                      />
                    </td>
                  ))}
                  <td className="px-2 py-1.5 text-center">
                    <button
                      onClick={() => handleDeleteRow(row.op_count)}
                      className="text-gray-300 hover:text-red-500 transition p-1"
                      title="Delete row"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
              {rates.length === 0 && (
                <tr>
                  <td colSpan={columns.length + 2} className="px-4 py-8 text-center text-gray-400">
                    No pricing tiers configured. Click "Add Row" to start.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {rates.length > 0 && (
          <p className="text-[11px] text-gray-400">
            {rates.length} tiers configured for {PRODUCTS.find(p => p.key === activeProduct)?.label}
            {deployments.length > 1 && ` — ${activeDeploymentMeta?.label}`}
          </p>
        )}
      </div>

      {/* Add Row Modal */}
      <Modal
        title="Add OP Count Row"
        open={addRowOpen}
        onCancel={() => setAddRowOpen(false)}
        onOk={handleAddRow}
        confirmLoading={addingRow}
        okText="Add Row"
      >
        <div className="py-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            OP visits per year
          </label>
          <InputNumber
            className="w-full"
            min={1}
            step={5000}
            value={newOpCount}
            onChange={setNewOpCount}
            placeholder="e.g. 25000"
            formatter={v => v ? Number(v).toLocaleString("en-IN") : ""}
            parser={v => v.replace(/[^\d]/g, "")}
          />
        </div>
      </Modal>
    </ContainerCard>
  );
}
